/**
 * Number validation utilities for form inputs
 * Simple validation logic that can be used directly with InputField
 */

/**
 * Strip cosmetic separators (comma, full stop) from a numeric string so it
 * can be parsed / validated. Full stop is treated as a separator rather
 * than a decimal point so formats like 1.000.000 work.
 * @param {string} value
 * @returns {string}
 */
const stripSeparators = (value) => {
  if (value === null || value === undefined) return '';
  return value.toString().replace(/[,.]/g, '');
};

const hasSeparators = (value) =>
  value !== null && value !== undefined && /[,.]/.test(value.toString());

// Pastes like ",500" are silently sanitized to "500" so we accept the user's
// intent instead of rejecting outright. Consecutive separators ("1,,000",
// "1.,0") stay rejected because the intent is ambiguous.
const stripLeadingSeparators = (value) => value.replace(/^[,.]+/, '');

const hasConsecutiveSeparators = (value) => {
  if (value === null || value === undefined) return false;
  return /[,.]{2}/.test(value.toString());
};

/**
 * Validates if a string represents a valid number format
 * @param {string} value - The string to validate
 * @returns {boolean} - True if valid number format
 */
export const isValidNumberFormat = (value) => {
  if (!value || value === '') return true;

  const normalized = stripSeparators(value);
  if (normalized === '') return true;

  return /^\d+$/.test(normalized);
};

/**
 * Validates if a value has invalid leading zeros
 * @param {string} value - The string to validate
 * @returns {boolean} - True if has invalid leading zeros
 */
export const hasInvalidLeadingZeros = (value) => {
  if (!value) return false;

  // Prevent leading zeros except for decimal numbers (e.g., "0.5" is allowed, "012" is not)
  const hasLeadingZero = /^0\d+$/.test(value); // "012", "03", etc.
  const isValidZeroDecimal = /^0\.\d*$/.test(value); // "0.5", "0.8", etc.

  return hasLeadingZero && !isValidZeroDecimal;
};

/**
 * Handles number input change validation
 * @param {string} value - The input value
 * @param {Function} setValue - Function to set the field value
 * @param {Function} triggerTooltip - Function to trigger tooltip message
 * @returns {string|number} - The processed value
 */
export const handleNumberInputChange = (value, setValue, triggerTooltip) => {
  if (!value || value === '') {
    setValue(null);
    return null;
  }

  // Strip anything that's not a digit or separator before validating. Catches
  // characters that bypass the keydown gate — IME composition, dictation,
  // autofill, macOS Option+key dead keys (˜, ™, ¢, …), pasted letters/symbols.
  const stringValue = value.toString();
  const onlyAllowedChars = stringValue.replace(/[^\d,.]/g, '');
  if (onlyAllowedChars !== stringValue) {
    triggerTooltip('Enter numbers only');
  }

  // Pastes that begin with separators ("," / ".") are sanitized rather than
  // rejected — the user's intent is clearly the digits.
  const sanitized = stripLeadingSeparators(onlyAllowedChars);
  if (sanitized === '') {
    setValue(null);
    return null;
  }
  value = sanitized;

  // Normalize for validation but keep the user's typed separators in state so
  // formats like 100,000 or 1.000.000 survive into downstream consumers
  // (e.g. offer letter tokens).
  const normalized = stripSeparators(value);
  const hasAnySeparator = hasSeparators(value);

  // Only flag leading zeros on values without cosmetic separators so inputs
  // like "0.5" or "0,5" (valid in some locales) aren't rejected.
  if (!hasAnySeparator && hasInvalidLeadingZeros(value)) {
    triggerTooltip('Invalid number format');
    return value;
  }

  if (!/^\d*$/.test(normalized)) {
    triggerTooltip('Enter numbers only');
    return value;
  }

  if (hasConsecutiveSeparators(value)) {
    triggerTooltip('Invalid number format');
    return value;
  }

  const numericValue = Number(normalized);
  if (normalized !== '' && !isNaN(numericValue)) {
    // Preserve the typed string when separators are present, or when the
    // value is outside the safe integer range — otherwise JS would re-render
    // it in scientific notation (e.g. 1.23e+24). Plain safe integers stay
    // typed as Number to keep the existing downstream contract.
    const keepAsString = hasAnySeparator || !Number.isSafeInteger(numericValue);
    const finalValue = keepAsString ? value : numericValue;
    setValue(finalValue);
    return finalValue;
  }

  setValue(value);
  return value;
};

/**
 * Handles number key down validation
 * @param {KeyboardEvent} e - The keyboard event
 * @param {Object} options - Validation options
 * @param {number} options.stepValue - Step value for arrow keys
 * @param {number} options.minValue - Minimum value
 * @param {number} options.maxValue - Maximum value
 * @param {Function} options.setValue - Function to set the field value
 * @returns {boolean} - True if event should be prevented
 */
export const handleNumberKeyDown = (
  e,
  { stepValue = 1, minValue, maxValue, setValue }
) => {
  const key = e?.key;
  const input = e.currentTarget;
  const { value, selectionStart } = input;

  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

  const step = stepValue ?? 1;
  const min = minValue ?? Number.MIN_SAFE_INTEGER;
  const max = maxValue ?? Number.MAX_SAFE_INTEGER;

  // Handle arrow keys for increment/decrement
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    e.preventDefault();
    let current = parseFloat(value);
    if (isNaN(current)) current = 0;
    const newValue = key === 'ArrowUp' ? current + step : current - step;
    const clampedValue = Math?.min(Math.max(newValue, min), max);
    setValue(Number(clampedValue));
    return true;
  }

  // Allow copy/paste operations
  if (
    (e?.ctrlKey || e?.metaKey) &&
    ['a', 'c', 'v', 'x', 'z'].includes(key?.toLowerCase())
  ) {
    return false;
  }

  // Block Option/Alt combos — on macOS Option+digit/letter emits dead keys
  // and special characters (˜, ™, ¢, …) that bypass the digit/separator gate.
  if (e?.altKey) {
    e.preventDefault();
    return true;
  }

  // Allow navigation keys
  if (allowedKeys.includes(key)) {
    return false;
  }

  // Allow cosmetic thousand separators (comma, full stop) at any position
  // after the first character. Full stop is treated as a separator (not a
  // decimal point) so formats like 1.000.000 are accepted. Block when the
  // character immediately before the cursor is also a separator so the user
  // can't type ",,", "..", ",.", "." — BE rejects those.
  if (key === ',' || key === '.') {
    if (selectionStart === 0) {
      e.preventDefault();
      return true;
    }
    const prevChar = value.charAt(selectionStart - 1);
    if (prevChar === ',' || prevChar === '.') {
      e.preventDefault();
      return true;
    }
    return false;
  }

  // Allow digits
  if (/^\d$/.test(key)) {
    // Block leading zero only when the field already has content (prevents
    // "012" while still allowing the user to type a standalone "0" or start
    // a decimal like "0.5" / "0,5" — the change handler's leading-zero rule
    // kicks in once a digit follows).
    if (key === '0' && selectionStart === 0 && value !== '') {
      e.preventDefault();
      return true;
    }
    return false;
  }

  // Block everything else (+, -, e/E, etc.)
  e.preventDefault();
  return true;
};

/**
 * Validates pasted content for number inputs
 * @param {ClipboardEvent} e - The paste event
 * @param {Function} triggerTooltip - Function to trigger tooltip message
 * @returns {boolean} - True if paste should be prevented
 */
export const handleNumberPaste = (e, triggerTooltip) => {
  const pasted = e?.clipboardData?.getData('text');
  // Accept pasted values containing cosmetic thousand separators (e.g.
  // "100,000", "1.000.000"); they're stripped before the numeric check.
  const normalized = stripSeparators(pasted);

  if (!/^\d+$/.test(normalized)) {
    e.preventDefault();
    triggerTooltip('Enter numbers only');
    return true;
  }

  // Leading separators are sanitized after the paste lands (handleInputChange
  // strips them); only block consecutive separators where intent is unclear.
  if (hasConsecutiveSeparators(pasted)) {
    e.preventDefault();
    triggerTooltip('Invalid number format');
    return true;
  }

  return false;
};

/**
 * Hook for number validation that can be used with InputField
 * @param {Object} options - Validation options
 * @returns {Object} - Validation functions and handlers
 */
export const useNumberValidation = (options = {}) => {
  const {
    stepValue = 1,
    minValue,
    maxValue,
    setValue,
    triggerTooltip,
  } = options;

  const handleInputChange = (value) => {
    return handleNumberInputChange(value, setValue, triggerTooltip);
  };

  const handleKeyDown = (e) => {
    return handleNumberKeyDown(e, {
      stepValue,
      minValue,
      maxValue,
      setValue,
    });
  };

  const handlePaste = (e) => {
    return handleNumberPaste(e, triggerTooltip);
  };

  return {
    handleInputChange,
    handleKeyDown,
    handlePaste,
    isValidNumberFormat,
    hasInvalidLeadingZeros,
  };
};

// Lightweight keydown gate for inputs that don't want the stepping behaviour
// of `handleNumberKeyDown` (e.g. KInputField, where ArrowUp/ArrowDown are
// reserved for cursor navigation). Returns true when the keypress is allowed,
// false when the caller should preventDefault.
const NUMBER_KEY_ALLOWED = /^[\d,.]$/;
const isSeparatorChar = (ch) => ch === ',' || ch === '.';
export const isNumberKeyAllowed = (e) => {
  // Ctrl/Cmd combos (copy/paste/select-all/etc.) pass through.
  if (e.ctrlKey || e.metaKey) return true;
  // Block macOS Option/Alt combos outright — Option+letter/digit emits dead
  // keys ("Dead", combining ˜) and special chars (™, ¢, …) which the
  // length/regex check below can't reliably catch.
  if (e.altKey) return false;
  const key = e.key;
  if (key.length !== 1) return true;
  if (!NUMBER_KEY_ALLOWED.test(key)) return false;
  if (!isSeparatorChar(key)) return true;
  const input = e.currentTarget;
  const pos = input.selectionStart ?? 0;
  if (pos === 0) return false;
  const prev = input.value.charAt(pos - 1);
  return !isSeparatorChar(prev);
};

export default useNumberValidation;
